define([
    'prop-types',
    'lodash',
    'componentsCore',
    'santa-components',
    'warmupUtilsLib',
    'mediaControls/mediaControlPlay/mediaControlPlay',
    'mediaControls/mediaControlFullscreen/mediaControlFullscreen',
    'mediaControls/mediaControlVolume/mediaControlVolume',
    'mediaControls/mediaControlProgress/mediaControlProgress',
    'mediaControls/mediaControlTime/mediaControlTime'
], function (PropTypes, _, componentsCore, santaComponents, warmupUtilsLib, mediaControlPlay, mediaControlFullscreen, mediaControlVolume, mediaControlProgress, mediaControlTime) {
    'use strict';

    const consts = warmupUtilsLib.mediaConsts;
    const nonVisibleVideoStates = {
        [consts.playbackTypes.WAITING]: true,
        [consts.playbackTypes.LOADING]: true,
        [consts.playbackTypes.READY]: true,
        [consts.playbackTypes.IDLE]: true
    };

    const showParams = {
        duration: 0.35,
        delay: 0.2,
        params: {autoAlpha: 1, ease: 'Sine.easeOut'}
    };

    const hideParams = {
        duration: 0.2,
        delay: 0.2,
        params: {opacity: 0, ease: 'Sine.easeIn'}
    };

    const CONTROLS_COMPS = {
        play: {
            componentName: 'wysiwyg.viewer.components.MediaControlPlay'
        },
        volume: {
            componentName: 'wysiwyg.viewer.components.MediaControlVolume'
        },
        fullscreen: {
            componentName: 'wysiwyg.viewer.components.MediaControlFullscreen'
        },
        progress: {
            componentName: 'wysiwyg.viewer.components.MediaControlProgress'
        },
        time: {
            componentName: 'wysiwyg.viewer.components.MediaControlTime'
        }
    };

    /**
     * @class components.MediaControls
     * @extends {core.skinBasedComp}
     */

    return {
        displayName: 'MediaControls',
        mixins: [componentsCore.mixins.skinBasedComp, santaComponents.mixins.animationsMixin, componentsCore.mixins.createChildComponentMixin],

        propTypes: _.defaults(
            {
                id: santaComponents.santaTypesDefinitions.Component.id.isRequired,
                mediaAspect: santaComponents.santaTypesDefinitions.SiteAspects.mediaAspect.isRequired,
                svgPropsMapForMediaControls: santaComponents.santaTypesDefinitions.VectorImage.svgPropsMapForMediaControls,
                compProp: santaComponents.santaTypesDefinitions.Component.compProp,
                compDesign: santaComponents.santaTypesDefinitions.Component.compDesign,
                playbackState: santaComponents.santaTypesDefinitions.Media.playbackState,
                fullscreen: santaComponents.santaTypesDefinitions.Media.fullscreen,
                volume: santaComponents.santaTypesDefinitions.Media.volume,
                muted: santaComponents.santaTypesDefinitions.Media.muted,
                controlsData: santaComponents.santaTypesDefinitions.Media.controlsData,
                componentViewMode: santaComponents.santaTypesDefinitions.RenderFlags.componentViewMode.isRequired,

                enableFullsceen: PropTypes.bool,
                playerInTimedHoverState: PropTypes.bool,
                playerInHoverState: PropTypes.bool
            },
            santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(mediaControlPlay),
            santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(mediaControlFullscreen),
            santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(mediaControlVolume),
            santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(mediaControlProgress),
            santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(mediaControlTime)
        ),

        getDefaultProps() {
            return {
                enableFullsceen: false,
                playerInHoverState: false
            };
        },

        componentDidUpdate(prevProps) { // eslint-disable-line complexity
            const showControlsInEditMode = this.props.playbackState && this.props.componentViewMode !== 'preview' && this.props.playbackState !== consts.playbackTypes.PLAYING;
            const showControlsOnHover = this.props.playbackState && !nonVisibleVideoStates[this.props.playbackState] && this.props.playerInTimedHoverState && this.props.playerInHoverState;

            const isPlaystateChanged = this.props.playbackState !== prevProps.playbackState;
            const isTimedHoverChanged = this.props.playerInTimedHoverState !== prevProps.playerInTimedHoverState;
            const isViewModeChanged = this.props.componentViewMode !== prevProps.componentViewMode;

            if (isTimedHoverChanged || isPlaystateChanged || isViewModeChanged) {
                const {duration, delay, params} = showControlsInEditMode || showControlsOnHover ? showParams : hideParams;
                this.animate('controls', 'BaseFade', duration, delay, params);
            }
        },

        /**
         *
         * @param storyboardUrl
         * @param videoAspectRatio
         */
        getProgressProps(storyboardUrl, videoAspectRatio) {
            const props = {
                showStoryboard: this.props.compProp.showStoryboard
            };
            return _.assign(props, {storyboardUrl, videoAspectRatio});
        },

        getControl(controlName, controlSpecificProps) {
            const compClass = CONTROLS_COMPS[controlName].componentName;
            const skinPart = controlName;
            const style = {
                height: this.props.style.height
            };

            const compProps = _.assign({
                ref: controlName,
                skinPart: controlName,
                id: this.props.id + controlName,
                playerId: this.props.compProp.playerId,
                svgPropsMapForMediaControls: this.props.svgPropsMapForMediaControls,
                style,
                className: this.classSet({control: true})
            }, controlSpecificProps);

            return this.createChildComponent(
                null,
                compClass,
                skinPart,
                compProps
            );
        },

        getSkinProperties() { // eslint-disable-line complexity
            const {play, volume: volumeComp, fullscreen: fullscreenComp, progress, time} = this.getSkinExports();
            const {hasAudio = true, disableAudio = false, storyboardUrl, videoAspectRatio} = this.props.controlsData;
            const {playbackState, fullscreen, volume = 1, muted} = this.props;
            const hidden = {style: {display: 'none'}};
            return {
                '': {
                    style: this.props.style,
                    'data-player-id': this.props.compProp.playerId,
                    'data-element-queries-def': 'controls'
                },
                controls: {
                    className: this.classSet({
                        'has-audio': hasAudio && !disableAudio,
                        controls: true
                    }),
                    onClick(event) {
                        // don't pass clicks to player mouse catcher
                        event.stopPropagation();
                    },
                    onTouchEnd(event) {
                        // don't pass touch to player mouse catcher
                        event.stopPropagation();
                    },
                    style: {
                        visibility: 'hidden'
                    }
                },

                play: play ? this.getControl('play', {playbackState}) : hidden,
                volume: volumeComp ? this.getControl('volume', {hasAudio, disableAudio, volume, muted}) : hidden,
                fullscreen: fullscreenComp && this.props.enableFullsceen ? this.getControl('fullscreen', {fullscreen}) : hidden,
                //TODO: update props on time update?
                progress: progress ? this.getControl('progress', this.getProgressProps(storyboardUrl, videoAspectRatio)) : hidden,
                time: time ? this.getControl('time') : hidden
            };
        }
    };
});
